import { memo } from "react";

import Tooltip from "@mui/joy/Tooltip";

/**
 * Props for Tooltip Component.
 * @interface
 * @name TooltipComponentProps
 * @kind interface
 */
interface TooltipComponentProps {
  /**
   * Children Component (`ReactElement`).
   * @property
   * @name children
   * @kind property
   * @memberof TooltipComponentProps
   * @type {React.ReactElement<any, string | React.JSXElementConstructor<any>>}
   */
  children: React.ReactElement;
  /**
   * Tooltip content. Zero-length content string,
   * undefined, null and false are never displayed.
   * @property
   * @name content
   * @kind property
   * @memberof TooltipComponentProps
   * @type {React.ReactNode}
   */
  content: React.ReactNode;
  /**
   * Id for the Tooltip Component.
   * @property
   * @name id
   * @kind property
   * @memberof TooltipComponentProps
   * @type {string}
   */
  id: string;
  /**
   * The `border-radius` CSS property rounds the
   * corners of an element's outer border edge.
   * {@link https://developer.mozilla.org/docs/Web/CSS/border-radius | More info.}
   * @property
   * @name borderRadius
   * @kind property
   * @memberof TooltipComponentProps
   * @type {string | undefined}
   */
  borderRadius?: string;
  /**
   * (optional)
   * If `true`, disables response to hover events.
   * @default false
   * @property
   * @name disableHoverListener
   * @kind property
   * @memberof TooltipComponentProps
   * @type {boolean | undefined}
   */
  disableHoverListener?: boolean | undefined;
  /**
   * (optional)
   * If `true`, the component is shown.
   * @property
   * @name open
   * @kind property
   * @memberof TooltipComponentProps
   * @type {boolean | undefined}
   */
  open?: boolean;
  /**
   * (optional)
   * Tooltip placement.
   * @default 'bottom'
   * @property
   * @name placement
   * @kind property
   * @memberof TooltipComponentProps
   * @type {"top" | "right" | "bottom" | "left" | "top-start" | "top-end" | "right-start" | "right-end" | "bottom-end" | "bottom-start" | "left-end" | "left-start" | undefined}
   */
  placement?:
    | "bottom"
    | "left"
    | "right"
    | "top"
    | "bottom-end"
    | "bottom-start"
    | "left-end"
    | "left-start"
    | "right-end"
    | "right-start"
    | "top-end"
    | "top-start"
    | undefined;
  /**
   * (optional)
   * The {@link https://mui.com/joy-ui/main-features/global-variants/ | global variant} to use.
   * @default 'solid'
   * @property
   * @name variant
   * @kind property
   * @memberof TooltipComponentProps
   * @type {"plain" | "outlined" | "soft" | "solid" | undefined}
   */
  variant?: "plain" | "outlined" | "soft" | "solid" | undefined;
}

/**
 * Memoized Tooltip Component.
 * @constant
 * @name TooltipComponent
 * @kind variable
 * @type {React.NamedExoticComponent<TooltipComponentProps>}
 */
const TooltipComponent: React.NamedExoticComponent<TooltipComponentProps> =
  memo(
    /**
     * Tooltip Component.
     * @function
     * @name TooltipComponent
     * @kind function
     * @memberof TooltipComponent
     * @param {TooltipComponentProps} props Props for Tooltip Component.
     * @returns {JSX.Element} Tooltip Component.
     */
    function TooltipComponent(props: TooltipComponentProps): JSX.Element {
      const {
        children,
        content,
        id,
        borderRadius,
        disableHoverListener,
        open,
        placement,
        variant,
      } = props;

      return (
        <Tooltip
          placement={placement}
          variant={variant}
          arrow
          title={content}
          disableHoverListener={disableHoverListener}
          open={open}
          id={id}
          sx={{
            backgroundColor: "#373737",
            borderRadius: borderRadius || "16px",
          }}
        >
          {children}
        </Tooltip>
      );
    }
  );

export default TooltipComponent;
